import React from 'react';
import {
  Box,
  Button,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { TerraformVariableSchema } from './types';
import { getDefaultForType } from './editors/utils';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrapper: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      width: '100%',
    },
    editorContainer: {
      flex: 1,
      minWidth: 0,
    },
    nullButton: {
      flexShrink: 0,
      minWidth: 'auto',
      padding: theme.spacing(0.5, 1),
      fontSize: '0.75rem',
      textTransform: 'none',
      marginTop: theme.spacing(0.5),
    },
    nullState: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.02)'
          : 'rgba(0, 0, 0, 0.02)',
      width: '100%',
    },
    nullLabel: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    nullText: {
      fontStyle: 'italic',
      color: theme.palette.text.secondary,
    },
    variableName: {
      fontWeight: 500,
    },
    editButton: {
      flexShrink: 0,
      minWidth: 'auto',
      padding: theme.spacing(0.5, 1.5),
      fontSize: '0.75rem',
      textTransform: 'none',
    },
  }),
);

interface NullableWrapperProps {
  variable: TerraformVariableSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  children: React.ReactNode;
}

/**
 * Wraps an editor with nullable handling:
 * - When value is null, shows "null" text and an "Edit" button instead of the editor
 * - Shows a "null" button on the right side for nullable variables (to set value to null)
 *
 * Note: Null values are always shown as a special null state, not as "null" string,
 * regardless of the nullable flag. The nullable flag only controls whether the
 * "null" button is shown to allow setting the value to null.
 *
 * Optional variables are implicitly nullable (can be set to null).
 */
export const NullableWrapper: React.FC<NullableWrapperProps> = ({
  variable,
  value,
  onChange,
  disabled,
  children,
}) => {
  const classes = useStyles();

  // Variables are implicitly nullable if:
  // - explicitly marked nullable
  // - marked as optional
  // - have a default value of null
  const isNullable = variable.nullable || variable.optional || variable.default === null;

  // If the value is null, always show the null state (never show null as a string)
  if (value === null) {
    const handleEdit = () => {
      // Restore to a default value based on the type
      const defaultValue = getDefaultForType(variable.type, variable.elementSchema);
      onChange(defaultValue);
    };

    return (
      <Box className={classes.nullState}>
        <Box className={classes.nullLabel}>
          <Typography variant="body2" className={classes.variableName}>
            {variable.name}
            {variable.required && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          <Typography variant="body2" className={classes.nullText}>
            null
          </Typography>
        </Box>
        {!disabled && (
          <Button
            variant="outlined"
            size="small"
            className={classes.editButton}
            onClick={handleEdit}
          >
            Edit
          </Button>
        )}
      </Box>
    );
  }

  // If the variable is not nullable (and not optional), just render the children without the null button
  if (!isNullable) {
    return <>{children}</>;
  }

  // If the value is not null and variable is nullable/optional, show the editor with a "null" button
  const handleSetNull = () => {
    onChange(null);
  };

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.editorContainer}>{children}</Box>
      {!disabled && (
        <Button
          variant="text"
          size="small"
          className={classes.nullButton}
          onClick={handleSetNull}
        >
          null
        </Button>
      )}
    </Box>
  );
};
